import { api } from '@/services/api/AxiosBaseService'
import type { PaginatedResponse, SingleResponse } from '@/types/api'
import type { Revenue } from '@/services/modules/RevenuesService'
import type { ImportPreview, ImportResult } from '@/components/EmployeeImportModal'
import { pollEmployeeImport } from '@/services/modules/employeeImportPolling'

export interface CompanyDashboard {
  activeCollaborators: number
  activeDependents: number
  totalHolders: number
  byPlanType: { planType: string; total: number }[]
}

export interface SimpleOption { id: string; name: string }
/** Departamento com as filiais (ids) que o compartilham — para filtrar por filial. */
export interface DepartmentOption extends SimpleOption { branchIds: string[] }

export const MyCompanyService = {
  async dashboard(): Promise<CompanyDashboard> {
    const r = await api.get<{ data: CompanyDashboard }>('/my-company/dashboard')
    return r.data.data
  },
  /** Filiais ativas da empresa (para selects). */
  async branches(): Promise<SimpleOption[]> {
    const r = await api.get<{ data: { id: string; systemName: string }[] }>('/my-company/branches', { params: { size: 0, status: 'ATIVO' } })
    return r.data.data.map(b => ({ id: b.id, name: b.systemName }))
  },
  /** Departamentos ativos da empresa (para selects), com as filiais que os compartilham. */
  async departments(): Promise<DepartmentOption[]> {
    const r = await api.get<{ data: { id: string; department: string; branchIds?: string[] }[] }>('/my-company/departments', { params: { size: 0, status: 'ATIVO' } })
    return r.data.data.map(d => ({ id: d.id, name: d.department, branchIds: d.branchIds ?? [] }))
  },

  /** Faturas da empresa (somente leitura). Mais recentes primeiro. */
  async invoices(params: Record<string, unknown> = {}): Promise<PaginatedResponse<Revenue>> {
    const r = await api.get<PaginatedResponse<Revenue>>('/my-company/invoices', { params })
    return r.data
  },
  async invoice(id: string): Promise<Revenue> {
    const r = await api.get<SingleResponse<Revenue>>(`/my-company/invoices/${id}`)
    return r.data.data
  },

  // ----- Importação de funcionários (empresa) -----
  async importTemplate(): Promise<Blob> {
    const r = await api.get('/my-company/collaborators/import-template', { responseType: 'blob' })
    return r.data as Blob
  },
  async importPreview(file: File): Promise<ImportPreview> {
    const fd = new FormData(); fd.append('file', file)
    const r = await api.post<{ data: ImportPreview }>('/my-company/collaborators/import-preview', fd)
    return r.data.data
  },
  async importConfirm(file: File): Promise<ImportResult> {
    // Assíncrono: dispara o job e acompanha o status (não segura a request 30s).
    const fd = new FormData(); fd.append('file', file)
    const r = await api.post<{ data: { id: string } }>('/my-company/collaborators/import-confirm', fd)
    return pollEmployeeImport(`/my-company/collaborators/import-status/${r.data.data.id}`)
  },
}
